import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  clubPostPrimaryMediaFrameClass,
  clubPostPrimaryMediaInnerBtnClass,
} from "./feedPostCardPresentation";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

const imgClassFeed =
  "block w-full max-w-full h-auto max-h-[min(260px,42vh)] sm:max-h-[min(340px,48vh)] object-contain pointer-events-none select-none";

const imgClassThread =
  "block w-full max-w-full h-auto max-h-[min(480px,50vh)] sm:max-h-[min(520px,62vh)] object-contain pointer-events-none select-none";

const arrowBtnClass =
  "absolute top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/55 text-white/80 transition hover:border-white/16 hover:bg-black/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/35";

/**
 * Reddit-style in-card gallery for club posts: one image at a time, arrows + dots.
 * State is internal per instance so feed cards do not share carousel index.
 *
 * @param {{
 *   images: string[],
 *   variant?: 'feed' | 'thread',
 *   className?: string,
 *   onOpenAt: (index: number) => void,
 * }} props
 */
export default function ClubPostMediaGallery({
  images,
  variant = "feed",
  className = "",
  onOpenAt,
}) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const n = list.length;
  const safeIndex = n === 0 ? 0 : Math.min(Math.max(0, index), n - 1);
  const current = list[safeIndex] || null;
  const imgCls = variant === "thread" ? imgClassThread : imgClassFeed;

  const listKey = list.join("\0");
  useEffect(() => {
    setIndex(0);
  }, [listKey]);

  const step = useCallback(
    (delta) => {
      if (n <= 1) return;
      setIndex((i) => (i + delta + n) % n);
    },
    [n]
  );

  const openCurrent = useCallback(
    (e) => {
      e.stopPropagation();
      if (current == null) return;
      onOpenAt?.(safeIndex);
    },
    [current, onOpenAt, safeIndex]
  );

  if (n === 0) return null;

  if (n === 1) {
    return (
      <div
        className={cn("relative", clubPostPrimaryMediaFrameClass, className)}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={openCurrent} className={clubPostPrimaryMediaInnerBtnClass}>
          <img src={list[0]} alt="" className={imgCls} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn("relative", clubPostPrimaryMediaFrameClass, className)}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative flex w-full min-h-[160px] items-center justify-center bg-[#080c12] sm:min-h-[200px]">
        <button
          type="button"
          onClick={openCurrent}
          className={cn(clubPostPrimaryMediaInnerBtnClass, "relative z-0 w-full")}
          aria-label={`Open image ${safeIndex + 1} of ${n}`}
        >
          <img src={current} alt="" className={imgCls} />
        </button>

        <button
          type="button"
          aria-label="Previous image"
          onClick={(e) => {
            e.stopPropagation();
            step(-1);
          }}
          className={cn(arrowBtnClass, "left-2 sm:left-2.5")}
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2} />
        </button>
        <button
          type="button"
          aria-label="Next image"
          onClick={(e) => {
            e.stopPropagation();
            step(1);
          }}
          className={cn(arrowBtnClass, "right-2 sm:right-2.5")}
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2} />
        </button>

        <div
          className="pointer-events-none absolute bottom-2.5 left-0 right-0 z-[2] flex justify-center gap-1.5"
          role="tablist"
          aria-label="Image position"
        >
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === safeIndex}
              onClick={(e) => {
                e.stopPropagation();
                setIndex(i);
              }}
              className={cn(
                "pointer-events-auto h-1.5 rounded-full transition-all duration-200",
                i === safeIndex ? "w-5 bg-white/55" : "w-1.5 bg-white/20 hover:bg-white/35"
              )}
              aria-label={`Image ${i + 1} of ${n}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
